import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  addEdge,
  Background,
  Controls,
  Handle,
  MarkerType,
  Panel,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type Node,
  type NodeProps,
  type NodeTypes,
  type ReactFlowInstance,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "@dagrejs/dagre";
import { apiFetch } from "../lib/api.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { type TaskRunItem, elapsed, statusPill, OutputModal } from "./ScheduleActivity.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { useToast } from "../components/Toast.js";
import { ScopeHeader } from "../components/ScopeHeader.js";
import { useMyGroups } from "../lib/scope.js";
import { NODE_LABELS, type OperationGraphNodeType } from "../lib/operationPorts.js";
import { isSchedulableNodeType, loadNodeCreationShapes } from "../lib/nodeCreationShapes.js";
import { CronPicker } from "../components/CronPicker.js";
import { cronToText, nextCronFire, utcISOToWallClock, wallClockToUtcISO } from "../lib/cronDisplay.js";
import type { BlackoutSpecV2 } from "../lib/blackoutSpec.js";

// =============================================================================
// Types
// =============================================================================

type TaskType = "operation" | "webhook" | "email" | "page" | "solve";
type EdgeCondition = "on_success" | "on_failure" | "on_completion";
type RetryStrategy = "immediate" | "fixed_delay" | "backoff" | "until_timeout";

type TaskDetail = {
  id: string;
  name: string;
  description: string | null;
  type: TaskType;
  timeout_min: number | null;
  retry_strategy: RetryStrategy | null;
  retry_config: Record<string, unknown> | null;
  operation: { task_id: string; operation_id: string | null; op_node_id: string; op_node_label: string | null; operation_name: string | null } | null;
  // ADR-0104 — "page" (Report) task config. page_title joined server-side.
  page: { task_id: string; page_id: string; page_title: string | null } | null;
  // ADR-0185 — "solve" task config. model_name/status joined server-side.
  solve: { task_id: string; model_id: string; model_name: string | null; model_status: string | null } | null;
};

// ADR-0104 — published pages for the Report picker (same source as subscriptions).
type PublishedPage = { id: string; title: string; route_key: string };

// ADR-0185 — optimization models for the Solve picker.
type OptimizeModelItem = { id: string; name: string; status: string };

type CanvasTask = {
  id: string;        // workflow_task.id (join table)
  task_id: string;
  position_x: number;
  position_y: number;
  task: TaskDetail;
};

type CanvasEdgeData = {
  id: string;
  from_task_id: string;
  to_task_id: string;
  condition: EdgeCondition;
};

/**
 * Where a NEW task lands — 4 across, one slot per existing task.
 *
 * Placement only; arranging an existing board is dagre's job (see Auto layout
 * below), because a grid cannot see edges. The pitch must clear the node box:
 * `.wfTaskNode` is max-width 240 plus 16px padding a side, so anything under
 * ~272 overlaps its neighbour.
 *
 * One owner for the numbers: the Add-task dialog places through here and the
 * hub's `add_schedule_task` mirrors the formula. That door used to send no
 * position at all, which the repo defaults to (0,0) — so several door-added
 * tasks stacked exactly on the origin, hidden under each other, and the
 * schedule looked like it had never gained them.
 */
const TASK_GRID_COLS = 4;
const TASK_GRID_X_PITCH = 300;
const TASK_GRID_Y_PITCH = 140;
const TASK_GRID_INSET = 50;

function taskGridPosition(index: number): { x: number; y: number } {
  return {
    x: (index % TASK_GRID_COLS) * TASK_GRID_X_PITCH + TASK_GRID_INSET,
    y: Math.floor(index / TASK_GRID_COLS) * TASK_GRID_Y_PITCH + TASK_GRID_INSET,
  };
}

type ScheduleStatus = "active" | "paused" | "disabled";

type BlackoutSchedule = {
  id: string;
  name: string;
  description: string;
  spec: BlackoutSpecV2;
  created_at: string;
  updated_at: string;
};


export type WorkflowDetail = {
  id: string;
  name: string;
  description: string | null;
  failure_mode: "stop" | "continue";
  timeout_min: number | null;
  default_retry_strategy: RetryStrategy | null;
  default_retry_config: Record<string, unknown> | null;
  schedule_cron: string | null;
  /** Count of completed workflow_runs since schedule_effective_date (server-derived). */
  succeeded_run_count_since_start: number;
  schedule_status: ScheduleStatus;
  schedule_effective_date: string | null;
  schedule_end_date: string | null;
  schedule_overlap: "block" | "allow";
  schedule_end_count: number | null;
  tasks: CanvasTask[];
  edges: CanvasEdgeData[];
  // ADR-0068 scope fields. Returned by the scope-visibility-filtered getter.
  scope?: import("../lib/scope.js").ContentScope;
  owner_account_id?: string | null;
  owner_group_id?: string | null;
  owner_group_name?: string | null;
};

type OpListItem = {
  id: string;
  name: string;
  kind: string;
  status: string;
};

type OpNode = {
  id: string;
  type: OperationGraphNodeType;
  label: string;
};

// ReactFlow types
type WfTaskNodeData = { taskId: string; name: string; type: TaskType; operationName: string | null; opNodeLabel: string | null; pageTitle: string | null; modelName: string | null };
type WfTaskNode = Node<WfTaskNodeData, "wfTask">;
type WfEdgeData = { edgeId: string; condition: EdgeCondition };
type WfEdge = Edge<WfEdgeData>;

// =============================================================================
// Helpers
// =============================================================================

function typeLabel(type: TaskType): string {
  return type === "operation" ? "Operation"
    : type === "webhook" ? "Webhook"
    : type === "email" ? "Email"
    : type === "solve" ? "Solve"
    : "Page refresh";
}

/** ADR-0104 — Report picker option label: "Title (slug)" — slug disambiguates duplicate titles. */
function pageOptionLabel(p: PublishedPage): string {
  return `${p.title} (${p.route_key})`;
}

function conditionLabel(cond: EdgeCondition): string {
  return cond === "on_success" ? "On success" : cond === "on_failure" ? "On failure" : "On completion";
}


/** Fetch schedulable op nodes from an operation's spec. */
async function loadOpNodes(operationId: string): Promise<OpNode[]> {
  const data = await apiFetch<{ operation: { spec: unknown } }>(
    `/api/tenant/operations/${encodeURIComponent(operationId)}`
  );
  const spec = data.operation.spec as Record<string, unknown> | null;
  if (!spec) return [];
  const graph = spec.graph as Record<string, unknown> | undefined;
  if (!graph) return [];
  const rawNodes = Array.isArray(graph.nodes) ? (graph.nodes as Record<string, unknown>[]) : [];
  // ADR-0154 §6.3 C4 — which step types can drive a scheduled run is the hub's
  // answer, served alongside the creation shapes. Ensure they are loaded before
  // filtering, or every node would be judged unschedulable on a cold cache.
  await loadNodeCreationShapes();
  return rawNodes
    .filter((n) => isSchedulableNodeType(String(n.type ?? "")))
    .map((n) => ({
      id: String(n.id ?? ""),
      type: n.type as OperationGraphNodeType,
      label: String(n.label ?? ""),
    }))
    .filter((n) => n.id);
}

function toRfNodes(tasks: CanvasTask[]): WfTaskNode[] {
  return tasks.map((ct) => ({
    id: ct.task_id,
    type: "wfTask" as const,
    position: { x: ct.position_x, y: ct.position_y },
    data: {
      taskId: ct.task_id,
      name: ct.task.name,
      type: ct.task.type,
      operationName: ct.task.operation?.operation_name ?? null,
      opNodeLabel: ct.task.operation?.op_node_label ?? null,
      pageTitle: ct.task.page?.page_title ?? null,
      modelName: ct.task.solve?.model_name ?? null,
    },
  }));
}

function toRfEdges(edges: CanvasEdgeData[]): WfEdge[] {
  return edges.map((ce) => ({
    id: ce.id,
    source: ce.from_task_id,
    target: ce.to_task_id,
    label: conditionLabel(ce.condition),
    markerEnd: { type: MarkerType.ArrowClosed },
    data: { edgeId: ce.id, condition: ce.condition },
  }));
}

// =============================================================================
// Custom ReactFlow node — defined outside component for stable nodeTypes ref
// =============================================================================

const WorkflowTaskNode = memo(function WorkflowTaskNode({
  data,
  selected,
}: NodeProps<WfTaskNode>) {
  const opLine = data.operationName
    ? [data.operationName, data.opNodeLabel].filter(Boolean).join(" / ")
    : null;
  const subLine = opLine ?? data.pageTitle ?? data.modelName;
  return (
    <div className={`wfTaskNode${selected ? " wfTaskNodeSelected" : ""}`}>
      <Handle type="target" position={Position.Left} id="in" />
      <div className="wfTaskNodeName">{data.name}</div>
      {subLine ? (
        <div className="wfTaskNodeOp">{subLine}</div>
      ) : null}
      <span className={`wfTaskNodeBadge wfTaskNodeBadge_${data.type}`}>
        {typeLabel(data.type)}
      </span>
      <Handle type="source" position={Position.Right} id="out" />
    </div>
  );
});

// Stable nodeTypes object (must not be recreated on each render)
const NODE_TYPES: NodeTypes = { wfTask: WorkflowTaskNode };

// =============================================================================
// Inspector sections
// =============================================================================

type InspectorTab = "workflow" | "history" | "task" | "edge";

// ---------------------------------------------------------------------------
// Workflow inspector
// ---------------------------------------------------------------------------

function WorkflowInspector(props: {
  workflow: WorkflowDetail;
  workflowId: string;
  timezone: string;
  onSaved: (wf: Partial<WorkflowDetail>) => void;
  onEditSchedule: () => void;
  onRunNow: () => void;
  busy: boolean;
  setBusy: (b: boolean) => void;
  toast: ReturnType<typeof useToast>;
}) {
  const { workflow, workflowId, timezone, onSaved, busy, setBusy, toast } = props;
  const [name, setName] = useState(workflow.name);
  const [description, setDescription] = useState(workflow.description ?? "");
  const [failureMode, setFailureMode] = useState(workflow.failure_mode);
  const [defaultRetry, setDefaultRetry] = useState(workflow.default_retry_strategy ?? "");
  const [blackoutNames, setBlackoutNames] = useState<string[]>([]);
  const [lastRun, setLastRun] = useState<{ status: string; started_at: string | null; completed_at: string | null } | null>(null);

  useEffect(() => {
    setName(workflow.name);
    setDescription(workflow.description ?? "");
    setFailureMode(workflow.failure_mode);
    setDefaultRetry(workflow.default_retry_strategy ?? "");
  }, [workflow]);

  useEffect(() => {
    apiFetch<{ blackout_schedules: { name: string }[] }>(
      `/api/tenant/workflows/${encodeURIComponent(workflowId)}/blackout-schedules`
    )
      .then((d) => setBlackoutNames(d.blackout_schedules.map((b) => b.name)))
      .catch(() => setBlackoutNames([]));
    apiFetch<{ last_run: { status: string; started_at: string | null; completed_at: string | null } | null }>(
      `/api/tenant/workflows/${encodeURIComponent(workflowId)}/last-run`
    )
      .then((d) => setLastRun(d.last_run))
      .catch(() => setLastRun(null));
  }, [workflowId, workflow]);

  async function saveField(field: string, value: unknown) {
    setBusy(true);
    try {
      await apiFetch(`/api/tenant/workflows/${encodeURIComponent(workflowId)}`, {
        method: "PATCH",
        body: { [field]: value },
      });
      onSaved({ [field]: value } as Partial<WorkflowDetail>);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="inspectorSection">
        <div className="inspectorSectionTitle">Details</div>
        <div style={{ display: "grid", gap: 10 }}>
          <label className="field">
            <span className="label">Name</span>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => { if (name.trim() && name !== workflow.name) void saveField("name", name.trim()); }}
              disabled={busy}
            />
          </label>
          <label className="field">
            <span className="label">Description</span>
            <input
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() => { if (description !== (workflow.description ?? "")) void saveField("description", description.trim() || null); }}
              disabled={busy}
            />
          </label>
        </div>
      </div>
      <div className="inspectorSection">
        <div className="inspectorSectionTitle">Execution</div>
        <div style={{ display: "grid", gap: 10 }}>
          <label className="field">
            <span className="label">On task failure</span>
            <select
              className="select"
              value={failureMode}
              onChange={(e) => {
                const v = e.target.value as "stop" | "continue";
                setFailureMode(v);
                void saveField("failure_mode", v);
              }}
              disabled={busy}
            >
              <option value="stop">Stop workflow</option>
              <option value="continue">Continue other branches</option>
            </select>
          </label>
          <label className="field">
            <span className="label">Default retry</span>
            <select
              className="select"
              value={defaultRetry}
              onChange={(e) => {
                setDefaultRetry(e.target.value);
                void saveField("default_retry_strategy", e.target.value || null);
              }}
              disabled={busy}
            >
              <option value="">None</option>
              <option value="immediate">Immediate</option>
              <option value="fixed_delay">Fixed delay</option>
              <option value="backoff">Exponential backoff</option>
              <option value="until_timeout">Until timeout</option>
            </select>
          </label>
        </div>
      </div>
      <div className="inspectorSection">
        <div className="inspectorSectionTitle">Schedule</div>

        {/* Status + summary on one line */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          {workflow.schedule_end_count != null
            && workflow.succeeded_run_count_since_start >= workflow.schedule_end_count ? (
            <span className="pill pillBad" title="Schedule has exceeded its run limit. Update Start date/time or remove the limit.">
              Limit reached ({workflow.succeeded_run_count_since_start}/{workflow.schedule_end_count})
            </span>
          ) : (
            <span className={`pill${workflow.schedule_status === "active" ? " pillOk" : workflow.schedule_status === "paused" ? " pillWarn" : ""}`}>
              {workflow.schedule_status === "active" ? "Active" : workflow.schedule_status === "paused" ? "Paused" : "Disabled"}
            </span>
          )}
          <span className="muted" style={{ fontSize: 12 }}>
            {workflow.schedule_cron
              ? scheduleSummary(formFromWorkflow(workflow, timezone), timezone)
              : "No schedule configured"}
          </span>
        </div>

        {(lastRun || (workflow.schedule_status === "active" && workflow.schedule_cron)) ? (
          <>
            <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: "8px 0" }} />
            {lastRun ? (
              <div className="muted" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                <span>Last run: {formatRunTs(lastRun.started_at ?? lastRun.completed_at)}</span>
                <span className={`pill${lastRun.status === "succeeded" ? " pillOk" : lastRun.status === "failed" || lastRun.status === "timed_out" ? " pillBad" : " pillWarn"}`} style={{ fontSize: 10, padding: "1px 5px" }}>
                  {lastRun.status}
                </span>
              </div>
            ) : null}
            {workflow.schedule_status === "active"
              && workflow.schedule_cron
              && !(workflow.schedule_end_count != null
                   && workflow.succeeded_run_count_since_start >= workflow.schedule_end_count)
              && (() => {
              const next = nextScheduledRun(formFromWorkflow(workflow, timezone), timezone);
              return next ? (
                <div className="muted" style={{ fontSize: 12 }}>Next run: {next}</div>
              ) : null;
            })()}
          </>
        ) : null}

        {blackoutNames.length > 0 ? (
          <>
            <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: "8px 0" }} />
            <div className="muted" style={{ fontSize: 12 }}>
              {blackoutNames.map((n) => (
                <div key={n} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ color: "var(--warning-text, #b45309)" }}>⊘</span> {n}
                </div>
              ))}
            </div>
          </>
        ) : null}

        <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: "8px 0" }} />
        <button
          className="btn"
          type="button"
          onClick={props.onEditSchedule}
          style={{ fontSize: 12 }}
        >
          Edit schedule
        </button>
      </div>
      <div className="inspectorFooter">
        <button
          className="btn btnPrimary"
          type="button"
          onClick={props.onRunNow}
          disabled={busy}
        >
          ▶ Run now
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// History inspector
// ---------------------------------------------------------------------------

type WorkflowRunSummary = {
  id: string;
  status: string;
  triggered_by: string;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
};

type WorkflowRunDetail = WorkflowRunSummary & {
  workflow_name: string;
  task_runs: TaskRunItem[];
};

function runStatusPillClass(status: string): string {
  if (status === "succeeded") return "pill pillOk";
  if (status === "failed" || status === "timed_out" || status === "cancelled") return "pill pillBad";
  if (status === "running" || status === "paused") return "pill pillWarn";
  return "pill";
}

function WorkflowRunDetailModal({
  workflowId,
  runId,
  onClose,
}: {
  workflowId: string;
  runId: string;
  onClose: () => void;
}) {
  const [run, setRun] = useState<WorkflowRunDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [outputTaskRun, setOutputTaskRun] = useState<TaskRunItem | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiFetch<{ workflow_run: WorkflowRunDetail }>(
      `/api/tenant/workflow-runs/${encodeURIComponent(runId)}`
    )
      .then((d) => setRun(d.workflow_run))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [runId]);

  return (
    <>
      <div className="modalOverlay" role="presentation" onClick={onClose}>
        <div
          className="modal modalScrollable"
          role="dialog"
          aria-modal="true"
          style={{ maxWidth: 640 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modalScrollHeader">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div className="reportModalTitle" style={{ flex: 1 }}>
                {run ? run.workflow_name : "Run Details"}
              </div>
              {run && statusPill(run.status)}
            </div>
            {run && (
              <div className="muted" style={{ fontSize: 12, marginTop: 4, display: "flex", gap: 12 }}>
                <span>{new Date(run.started_at ?? run.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true })}</span>
                <span>{elapsed(run.started_at, run.completed_at)}</span>
              </div>
            )}
          </div>

          <div className="modalScrollBody">
            {loading && <div className="muted" style={{ fontSize: 13 }}>Loading…</div>}
            {error && <div style={{ color: "var(--danger-text)", fontSize: 13 }}>{error}</div>}
            {!loading && !error && run && run.task_runs.length === 0 && (
              <div className="muted" style={{ fontSize: 13 }}>No task runs recorded.</div>
            )}
            {!loading && !error && run && run.task_runs.length > 0 && (
              run.task_runs.map((tr) => (
                <div
                  key={tr.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "5px 10px",
                    borderBottom: "1px solid var(--border)",
                    fontSize: 12,
                  }}
                >
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {tr.operation_id && tr.operation_name ? (
                      <>
                        <Link to={`/app/schedule/workflows/${workflowId}?task=${tr.task_id}`} style={{ fontWeight: 600 }}>
                          {tr.task_name}
                        </Link>
                        <span className="muted"> · {tr.operation_name}{tr.op_node_label ? ` / ${tr.op_node_label}` : ""}</span>
                      </>
                    ) : (
                      <Link to={`/app/schedule/workflows/${workflowId}?task=${tr.task_id}`} style={{ fontWeight: 600 }}>
                        {tr.task_name}
                      </Link>
                    )}
                  </span>
                  <span className="muted" style={{ flexShrink: 0 }}>{elapsed(tr.started_at, tr.completed_at)}</span>
                  {statusPill(tr.status, tr.status === "cancelled" && tr.started_at === null)}
                  <button
                    className="adminListAction"
                    type="button"
                    style={{ fontSize: 11, flexShrink: 0 }}
                    onClick={() => setOutputTaskRun(tr)}
                  >
                    Output
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="modalScrollFooter">
            <div className="actions">
              <button className="btn" type="button" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
      {outputTaskRun && (
        <OutputModal taskRun={outputTaskRun} onClose={() => setOutputTaskRun(null)} />
      )}
    </>
  );
}

function WorkflowHistoryInspector({ workflowId }: { workflowId: string }) {
  const [runs, setRuns] = useState<WorkflowRunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiFetch<{ runs: WorkflowRunSummary[] }>(
      `/api/tenant/workflows/${encodeURIComponent(workflowId)}/runs?limit=25`
    )
      .then((d) => setRuns(d.runs))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [workflowId]);

  return (
    <div className="inspectorSection">
      <div className="inspectorSectionTitle">Run History</div>
      {loading && <div className="muted" style={{ fontSize: 13 }}>Loading…</div>}
      {error && <div style={{ color: "var(--danger-text)", fontSize: 13 }}>{error}</div>}
      {!loading && !error && runs.length === 0 && (
        <div className="muted" style={{ fontSize: 13 }}>No runs yet.</div>
      )}
      {!loading && !error && runs.length > 0 && (
        <div style={{ display: "grid", gap: 2 }}>
          {runs.map((run) => (
            <button
              key={run.id}
              type="button"
              onClick={() => setSelectedRunId(run.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "5px 6px",
                background: "none",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
                textAlign: "left",
                width: "100%",
                fontSize: 12,
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--bg-subtle)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "none"; }}
            >
              <span className={runStatusPillClass(run.status)} style={{ fontSize: 10, padding: "1px 5px", flexShrink: 0 }}>
                {run.status}
              </span>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} className="muted">
                {formatRunTs(run.started_at ?? run.created_at)}
              </span>
              <span className="muted" style={{ flexShrink: 0 }}>{elapsed(run.started_at, run.completed_at)}</span>
            </button>
          ))}
        </div>
      )}
      {selectedRunId && (
        <WorkflowRunDetailModal
          workflowId={workflowId}
          runId={selectedRunId}
          onClose={() => setSelectedRunId(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Schedule inspector
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Schedule timing form helpers
// ---------------------------------------------------------------------------

type ScheduleForm = {
  schedule_cron: string | null;
  start_date: string; // YYYY-MM-DD — first occurrence date
  start_time: string; // HH:MM — first occurrence time
  end_mode: "none" | "on_date" | "after_n";
  end_date: string;
  end_count: number;
  overlap: "block" | "allow";
};

function defaultScheduleForm(): ScheduleForm {
  return {
    schedule_cron: null,
    start_date: "",
    start_time: "",
    end_mode: "none",
    end_date: "",
    end_count: 5,
    overlap: "block",
  };
}

export type ScheduleFields = Pick<WorkflowDetail,
  "schedule_cron" | "schedule_effective_date" |
  "schedule_end_date" | "schedule_end_count" | "schedule_overlap"
>;

export function formFromWorkflow(wf: ScheduleFields, tenantTz: string): ScheduleForm {
  // schedule_effective_date is TIMESTAMPTZ (migration 122). Convert the UTC
  // instant back to tenant-tz wall-clock so the user sees the same date/time
  // they originally typed regardless of their browser's tz.
  const { date: start_date, time: start_time } = utcISOToWallClock(wf.schedule_effective_date, tenantTz);
  return {
    schedule_cron: wf.schedule_cron,
    start_date,
    start_time,
    end_mode: wf.schedule_end_date ? "on_date" : (wf.schedule_end_count != null ? "after_n" : "none"),
    end_date: wf.schedule_end_date ? wf.schedule_end_date.slice(0, 10) : "",
    end_count: wf.schedule_end_count ?? 5,
    overlap: wf.schedule_overlap ?? "block",
  };
}

function buildScheduleBody(form: ScheduleForm, status: ScheduleStatus, tenantTz: string) {
  // Convert tenant-tz wall-clock to UTC ISO before persisting. Date-only
  // (start_date with no start_time) anchors at midnight tenant-local.
  const effectiveDate = form.start_date && form.start_time
    ? wallClockToUtcISO(form.start_date, form.start_time, tenantTz)
    : (form.start_date ? wallClockToUtcISO(form.start_date, "00:00", tenantTz) : null);
  return {
    schedule_cron: form.schedule_cron,
    schedule_status: status,
    schedule_effective_date: effectiveDate || null,
    schedule_end_date: form.end_mode === "on_date" ? form.end_date || null : null,
    schedule_end_count: form.end_mode === "after_n" ? form.end_count : null,
    schedule_overlap: form.overlap,
  };
}

export function scheduleSummary(form: ScheduleForm, timezone: string): string {
  let base = cronToText(form.schedule_cron);
  if (form.schedule_cron) base += ` (${timezone})`;
  if (form.end_mode === "on_date" && form.end_date) {
    base += `, until ${form.end_date}`;
  } else if (form.end_mode === "after_n") {
    base += `, for ${form.end_count} run${form.end_count !== 1 ? "s" : ""}`;
  }
  return base;
}

function formatRunTs(ts: string | null): string {
  if (!ts) return "—";
  const d = new Date(ts);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true });
  }
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
    " at " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true });
}

function nextScheduledRun(form: ScheduleForm, timezone: string): string | null {
  const next = nextCronFire(form.schedule_cron, timezone, {
    endDate: form.end_mode === "on_date" ? form.end_date || null : null,
  });
  return next ? formatNextRun(next, timezone) : null;
}

function formatNextRun(date: Date, timezone: string): string {
  return date.toLocaleString("en-US", {
    timeZone: timezone,
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}


// ---------------------------------------------------------------------------
// Schedule modal
// ---------------------------------------------------------------------------

export function ScheduleModal(props: {
  workflow: WorkflowDetail;
  workflowId: string;
  timezone: string;
  onSaved: (wf: Partial<WorkflowDetail>) => void;
  onClose: () => void;
}) {
  const { workflow, workflowId, timezone, onSaved, onClose } = props;
  const toast = useToast();
  const [form, setForm] = useState<ScheduleForm>(formFromWorkflow(workflow, timezone));
  const [status, setStatus] = useState<ScheduleStatus>(workflow.schedule_status);
  const [busy, setBusy] = useState(false);
  const [allBlackouts, setAllBlackouts] = useState<{ id: string; name: string }[]>([]);
  const [selectedBlackoutIds, setSelectedBlackoutIds] = useState<string[]>([]);
  const [blackoutsLoading, setBlackoutsLoading] = useState(true);

  useEffect(() => {
    async function loadBlackouts() {
      try {
        const [allData, wfData] = await Promise.all([
          apiFetch<{ blackout_schedules: { id: string; name: string }[] }>("/api/tenant/blackout-schedules"),
          apiFetch<{ blackout_schedules: { id: string }[] }>(`/api/tenant/workflows/${encodeURIComponent(workflowId)}/blackout-schedules`),
        ]);
        setAllBlackouts(allData.blackout_schedules);
        setSelectedBlackoutIds(wfData.blackout_schedules.map((b) => b.id));
      } catch {
        // non-fatal — section just won't populate
      } finally {
        setBlackoutsLoading(false);
      }
    }
    void loadBlackouts();
  }, [workflowId]);

  async function save() {
    setBusy(true);
    try {
      const body = buildScheduleBody(form, status, timezone);
      const [schedData] = await Promise.all([
        apiFetch<{ workflow: WorkflowDetail }>(
          `/api/tenant/workflows/${encodeURIComponent(workflowId)}`,
          { method: "PATCH", body }
        ),
        apiFetch(`/api/tenant/workflows/${encodeURIComponent(workflowId)}/blackout-schedules`, {
          method: "PUT",
          body: { blackout_ids: selectedBlackoutIds },
        }),
      ]);
      onSaved(schedData.workflow);
      onClose();
      toast.push({ kind: "success", message: "Schedule saved." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  const summary = scheduleSummary(form, timezone);

  return (
    <div className="modalOverlay" role="presentation" onClick={onClose}>
      <div
        className="modal modalScrollable"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sched-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 520 }}
      >
        <div className="modalScrollHeader">
          <div className="reportModalTitle" id="sched-modal-title">Edit Schedule</div>
        </div>
        <div className="modalScrollBody" style={{ paddingTop: 12, paddingBottom: 12 }}>
        <div style={{ display: "grid", gap: 12 }}>
          {workflow.schedule_end_count != null
            && workflow.succeeded_run_count_since_start >= workflow.schedule_end_count ? (
            <div className="help" style={{
              padding: "10px 12px",
              borderRadius: 6,
              background: "var(--danger-bg, #fff5f5)",
              border: "1px solid var(--danger-text, #c0392b)",
              color: "var(--danger-text, #c0392b)",
              fontSize: 13,
              lineHeight: 1.5,
            }}>
              <strong>⚠ Schedule has exceeded its run limit ({workflow.succeeded_run_count_since_start}/{workflow.schedule_end_count}) and stopped.</strong>
              <div style={{ marginTop: 4 }}>
                To re-enable, update the <strong>Start date/time</strong> below (counter restarts at zero) or set <strong>End → No end</strong>.
              </div>
            </div>
          ) : null}
          <label className="field">
            <span className="label">Status</span>
            <select
              className="select"
              value={status}
              onChange={(e) => setStatus(e.target.value as ScheduleStatus)}
              disabled={busy}
            >
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="disabled">Disabled</option>
            </select>
          </label>

          <div className="field">
            <span className="label">Schedule</span>
            <CronPicker
              value={{ cron: form.schedule_cron, start_date: form.start_date, start_time: form.start_time }}
              onChange={(next) => setForm((prev) => ({
                ...prev,
                schedule_cron: next.cron,
                start_date: next.start_date,
                start_time: next.start_time,
              }))}
              tenantTz={timezone}
            />
          </div>


          <div>
            <div className="label" style={{ marginBottom: 6 }}>End</div>
            <div style={{ display: "grid", gap: 6 }}>
              {(["none", "on_date", "after_n"] as const).map((mode) => (
                <label key={mode} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                  <input
                    type="radio"
                    name="sched_end_mode"
                    value={mode}
                    checked={form.end_mode === mode}
                    onChange={() => setForm((prev) => ({ ...prev, end_mode: mode }))}
                    disabled={busy}
                  />
                  {mode === "none" ? "No end" : mode === "on_date" ? "End on date" : "End after N runs"}
                </label>
              ))}
              {form.end_mode === "on_date" ? (
                <input
                  className="input"
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm((prev) => ({ ...prev, end_date: e.target.value }))}
                  disabled={busy}
                  style={{ marginTop: 4 }}
                />
              ) : null}
              {form.end_mode === "after_n" ? (
                <input
                  className="input"
                  type="number"
                  min={1}
                  value={form.end_count}
                  onChange={(e) => setForm((prev) => ({ ...prev, end_count: parseInt(e.target.value, 10) || 1 }))}
                  disabled={busy}
                  style={{ marginTop: 4 }}
                />
              ) : null}
            </div>
          </div>

          <label className="field">
            <span className="label">If already running</span>
            <select
              className="select"
              value={form.overlap}
              onChange={(e) => setForm((prev) => ({ ...prev, overlap: e.target.value as "block" | "allow" }))}
              disabled={busy}
            >
              <option value="block">Skip — don't start a new run</option>
              <option value="allow">Allow — start a new run anyway</option>
            </select>
          </label>

          <div className="help">{summary}</div>

          <div>
            <div className="label" style={{ marginBottom: 6 }}>Blackout windows</div>
            {blackoutsLoading ? (
              <div className="muted" style={{ fontSize: 13 }}>Loading…</div>
            ) : allBlackouts.length === 0 ? (
              <div className="muted" style={{ fontSize: 13 }}>No blackout schedules configured. <a href="/app/schedule/blackouts" target="_blank" rel="noreferrer">Add one</a>.</div>
            ) : (
              <div style={{ display: "grid", gap: 6 }}>
                {allBlackouts.map((b) => (
                  <label key={b.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={selectedBlackoutIds.includes(b.id)}
                      disabled={busy}
                      onChange={(e) => {
                        setSelectedBlackoutIds((prev) =>
                          e.target.checked ? [...prev, b.id] : prev.filter((id) => id !== b.id)
                        );
                      }}
                    />
                    {b.name}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        </div>

        <div className="modalScrollFooter">
          <div className="actions">
            <button className="btn" type="button" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn btnPrimary" type="button" onClick={() => void save()} disabled={busy}>
              Save schedule
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Task inspector
// ---------------------------------------------------------------------------

function TaskInspector(props: {
  task: CanvasTask;
  workflowId: string;
  operations: OpListItem[];
  publishedPages: PublishedPage[];
  optimizeModels: OptimizeModelItem[];
  onSaved: (fields: Record<string, unknown>) => void;
  onDelete: () => void;
  busy: boolean;
  setBusy: (b: boolean) => void;
  toast: ReturnType<typeof useToast>;
}) {
  const { task, workflowId, onSaved, onDelete, busy, setBusy, toast } = props;
  const [name, setName] = useState(task.task.name);
  const [timeout, setTimeout] = useState(String(task.task.timeout_min ?? ""));
  const [retryStrategy, setRetryStrategy] = useState(task.task.retry_strategy ?? "");

  // Two-step operation node picker
  const [selectedOpId, setSelectedOpId] = useState(task.task.operation?.operation_id ?? "");
  const [opNodeId, setOpNodeId] = useState(task.task.operation?.op_node_id ?? "");
  const [opNodes, setOpNodes] = useState<OpNode[]>([]);
  const [loadingNodes, setLoadingNodes] = useState(false);

  // ADR-0104 — Report (page) picker
  const [pageId, setPageId] = useState(task.task.page?.page_id ?? "");

  // ADR-0185 — Solve (optimization model) picker
  const [solveModelId, setSolveModelId] = useState(task.task.solve?.model_id ?? "");

  useEffect(() => {
    setName(task.task.name);
    setTimeout(String(task.task.timeout_min ?? ""));
    setRetryStrategy(task.task.retry_strategy ?? "");
    setPageId(task.task.page?.page_id ?? "");
    setSolveModelId(task.task.solve?.model_id ?? "");
    const opId = task.task.operation?.operation_id ?? "";
    setSelectedOpId(opId);
    setOpNodeId(task.task.operation?.op_node_id ?? "");
    setOpNodes([]);
    if (opId) {
      setLoadingNodes(true);
      loadOpNodes(opId)
        .then(setOpNodes)
        .catch(() => setOpNodes([]))
        .finally(() => setLoadingNodes(false));
    }
  }, [task]);

  async function onPageChange(newPageId: string) {
    setPageId(newPageId);
    if (!newPageId) return;
    await saveField({ page_id: newPageId });
  }

  async function onSolveModelChange(newModelId: string) {
    setSolveModelId(newModelId);
    if (!newModelId) return;
    await saveField({ model_id: newModelId });
  }

  async function onOperationChange(opId: string) {
    setSelectedOpId(opId);
    setOpNodeId("");
    setOpNodes([]);
    if (!opId) return;
    setLoadingNodes(true);
    try {
      const nodes = await loadOpNodes(opId);
      setOpNodes(nodes);
    } catch {
      toast.push({ kind: "error", message: "Could not load operation nodes." });
    } finally {
      setLoadingNodes(false);
    }
  }

  async function onNodeChange(nodeId: string) {
    setOpNodeId(nodeId);
    if (!nodeId) return;
    await saveField({ operation_id: selectedOpId, op_node_id: nodeId });
  }

  async function saveField(fields: Record<string, unknown>) {
    setBusy(true);
    try {
      await apiFetch(
        `/api/tenant/workflows/${encodeURIComponent(workflowId)}/tasks/${encodeURIComponent(task.task_id)}`,
        { method: "PATCH", body: fields }
      );
      onSaved(fields);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="inspectorSection">
        <div className="inspectorSectionTitle">Task</div>
        <div style={{ display: "grid", gap: 10 }}>
          <label className="field">
            <span className="label">Name</span>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => { if (name.trim() && name !== task.task.name) void saveField({ name: name.trim() }); }}
              disabled={busy}
            />
          </label>
          <div className="field">
            <span className="label">Type</span>
            <div style={{ fontSize: 13, padding: "6px 0" }}>
              <span className={`wfTaskNodeBadge wfTaskNodeBadge_${task.task.type}`}>
                {typeLabel(task.task.type)}
              </span>
            </div>
          </div>
          {task.task.type === "operation" ? (
            <>
              <label className="field">
                <span className="label">Operation</span>
                <select
                  className="select"
                  value={selectedOpId}
                  onChange={(e) => void onOperationChange(e.target.value)}
                  disabled={busy}
                >
                  <option value="">— select operation —</option>
                  {props.operations.map((op) => (
                    <option key={op.id} value={op.id}>{op.name}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="label">Node to run</span>
                <select
                  className="select"
                  value={opNodeId}
                  onChange={(e) => void onNodeChange(e.target.value)}
                  disabled={busy || !selectedOpId || loadingNodes}
                >
                  <option value="">
                    {loadingNodes ? "Loading…" : selectedOpId ? "— select node —" : "— pick operation first —"}
                  </option>
                  {opNodes.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.label ? `${n.label} (${NODE_LABELS[n.type] ?? n.type})` : (NODE_LABELS[n.type] ?? n.type)}
                    </option>
                  ))}
                </select>
                {selectedOpId && !loadingNodes && opNodes.length === 0 && (
                  <div className="help">No schedulable nodes found in this operation.</div>
                )}
                {selectedOpId && opNodeId && (
                  <div className="help" style={{ marginTop: 4 }}>
                    <Link
                      to={`/app/build/operations/${encodeURIComponent(selectedOpId)}?node=${encodeURIComponent(opNodeId)}`}
                    >
                      Open this node in the operation editor →
                    </Link>
                  </div>
                )}
              </label>
            </>
          ) : task.task.type === "page" ? (
            <label className="field">
              <span className="label">Page (published)</span>
              <select
                className="select"
                value={pageId}
                onChange={(e) => void onPageChange(e.target.value)}
                disabled={busy}
              >
                <option value="">Select a published page…</option>
                {props.publishedPages.map((p) => (
                  <option key={p.id} value={p.id}>{pageOptionLabel(p)}</option>
                ))}
              </select>
              {pageId && !props.publishedPages.some((p) => p.id === pageId) && (
                <div className="help">{task.task.page?.page_title ?? "Selected page is no longer published."}</div>
              )}
            </label>
          ) : task.task.type === "solve" ? (
            <label className="field">
              <span className="label">Optimization model</span>
              <select
                className="select"
                value={solveModelId}
                onChange={(e) => void onSolveModelChange(e.target.value)}
                disabled={busy}
              >
                <option value="">Select a model…</option>
                {props.optimizeModels.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
              {solveModelId && !props.optimizeModels.some((m) => m.id === solveModelId) && (
                <div className="help" style={{ color: "var(--danger-text)" }}>
                  The model this task solves no longer exists — pick another, or remove the task.
                </div>
              )}
              {task.task.solve?.model_status === "disabled" && (
                <div className="help" style={{ color: "var(--danger-text)" }}>
                  This model is disabled — the solve will refuse when the schedule fires.
                </div>
              )}
              <div className="help" style={{ marginTop: 4 }}>
                Runs a standing solve on fire; the task succeeds only when a fresh plan lands.
              </div>
            </label>
          ) : null}
          <label className="field">
            <span className="label">Timeout (minutes, optional)</span>
            <input
              className="input"
              type="number"
              min={1}
              value={timeout}
              onChange={(e) => setTimeout(e.target.value)}
              onBlur={() => {
                const v = timeout ? parseInt(timeout, 10) : null;
                void saveField({ timeout_min: v });
              }}
              disabled={busy}
            />
          </label>
          <label className="field">
            <span className="label">Retry override</span>
            <select
              className="select"
              value={retryStrategy}
              onChange={(e) => {
                setRetryStrategy(e.target.value);
                void saveField({ retry_strategy: e.target.value || null });
              }}
              disabled={busy}
            >
              <option value="">Use workflow default</option>
              <option value="immediate">Immediate</option>
              <option value="fixed_delay">Fixed delay</option>
              <option value="backoff">Exponential backoff</option>
              <option value="until_timeout">Until timeout</option>
            </select>
          </label>
        </div>
      </div>
      <div className="inspectorFooter">
        <button
          className="btn btnDanger"
          type="button"
          onClick={onDelete}
          disabled={busy}
        >
          Delete task
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Edge inspector
// ---------------------------------------------------------------------------

function EdgeInspector(props: {
  edge: CanvasEdgeData;
  workflowId: string;
  onSaved: (condition: EdgeCondition) => void;
  onDelete: () => void;
  busy: boolean;
  setBusy: (b: boolean) => void;
  toast: ReturnType<typeof useToast>;
}) {
  const { edge, workflowId, onSaved, onDelete, busy, setBusy, toast } = props;

  async function saveCondition(condition: EdgeCondition) {
    setBusy(true);
    try {
      await apiFetch(
        `/api/tenant/workflows/${encodeURIComponent(workflowId)}/edges/${encodeURIComponent(edge.id)}`,
        { method: "PATCH", body: { condition } }
      );
      onSaved(condition);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="inspectorSection">
        <div className="inspectorSectionTitle">Edge condition</div>
        <div style={{ display: "grid", gap: 10 }}>
          <div className="muted" style={{ fontSize: 12 }}>
            Run the next task when the preceding task completes with:
          </div>
          <label className="field">
            <span className="label">Condition</span>
            <select
              className="select"
              value={edge.condition}
              onChange={(e) => void saveCondition(e.target.value as EdgeCondition)}
              disabled={busy}
            >
              <option value="on_success">On success</option>
              <option value="on_failure">On failure</option>
              <option value="on_completion">On completion (any)</option>
            </select>
          </label>
        </div>
      </div>
      <div className="inspectorFooter">
        <button className="btn btnDanger" type="button" onClick={onDelete} disabled={busy}>
          Remove connection
        </button>
      </div>
    </>
  );
}

// =============================================================================
// Add Task Modal
// =============================================================================

function AddTaskModal(props: {
  workflowId: string;
  operations: OpListItem[];
  publishedPages: PublishedPage[];
  optimizeModels: OptimizeModelItem[];
  existingTaskCount: number;
  onCreated: (ct: CanvasTask) => void;
  onClose: () => void;
}) {
  const { workflowId, operations, publishedPages, optimizeModels, existingTaskCount, onCreated, onClose } = props;
  const toast = useToast();
  const [name, setName] = useState("");
  const [type, setType] = useState<TaskType>("operation");

  // Two-step operation node picker
  const [selectedOpId, setSelectedOpId] = useState("");
  const [opNodeId, setOpNodeId] = useState("");
  const [opNodes, setOpNodes] = useState<OpNode[]>([]);
  const [loadingNodes, setLoadingNodes] = useState(false);

  // ADR-0104 — Report (page) picker
  const [selectedPageId, setSelectedPageId] = useState("");

  // ADR-0185 — Solve (optimization model) picker
  const [selectedModelId, setSelectedModelId] = useState("");

  const [busy, setBusy] = useState(false);

  async function onOperationChange(opId: string) {
    setSelectedOpId(opId);
    setOpNodeId("");
    setOpNodes([]);
    if (!opId) return;
    setLoadingNodes(true);
    try {
      const nodes = await loadOpNodes(opId);
      setOpNodes(nodes);
    } catch {
      toast.push({ kind: "error", message: "Could not load operation nodes." });
    } finally {
      setLoadingNodes(false);
    }
  }

  async function create() {
    if (!name.trim()) return;
    if (type === "operation" && !opNodeId) return;
    if (type === "page" && !selectedPageId) return;
    if (type === "solve" && !selectedModelId) return;
    setBusy(true);
    try {
      // Place new node in a grid pattern
      const { x: px, y: py } = taskGridPosition(existingTaskCount);

      const data = await apiFetch<{ workflow_task: CanvasTask }>(
        `/api/tenant/workflows/${encodeURIComponent(workflowId)}/tasks`,
        {
          method: "POST",
          body: {
            name: name.trim(),
            type,
            operation_id: type === "operation" ? selectedOpId : undefined,
            op_node_id: type === "operation" ? opNodeId : undefined,
            page_id: type === "page" ? selectedPageId : undefined,
            model_id: type === "solve" ? selectedModelId : undefined,
            position_x: px,
            position_y: py,
          },
        }
      );
      onCreated(data.workflow_task);
      onClose();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  const valid =
    name.trim() &&
    (type !== "operation" || (selectedOpId && opNodeId)) &&
    (type !== "page" || !!selectedPageId) &&
    (type !== "solve" || !!selectedModelId);

  return (
    <div className="modalOverlay" role="presentation" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="reportModalTitle">Add task</div>
        <div style={{ display: "grid", gap: 12 }}>
          <label className="field">
            <span className="label">Name</span>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="e.g. Load accounts"
              onKeyDown={(e) => { if (e.key === "Enter" && valid) void create(); }}
              disabled={busy}
            />
          </label>
          <label className="field">
            <span className="label">Type</span>
            <select
              className="select"
              value={type}
              onChange={(e) => {
                setType(e.target.value as TaskType);
                setSelectedOpId("");
                setOpNodeId("");
                setOpNodes([]);
                setSelectedPageId("");
                setSelectedModelId("");
              }}
              disabled={busy}
            >
              <option value="operation">Operation</option>
              <option value="page">Page refresh</option>
              <option value="solve">Solve</option>
              <option value="webhook">Webhook</option>
              <option value="email">Email</option>
            </select>
          </label>
          {type === "operation" ? (
            <>
              <label className="field">
                <span className="label">Operation</span>
                <select
                  className="select"
                  value={selectedOpId}
                  onChange={(e) => void onOperationChange(e.target.value)}
                  disabled={busy}
                >
                  <option value="">— select operation —</option>
                  {operations.map((op) => (
                    <option key={op.id} value={op.id}>{op.name}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="label">Node to run</span>
                <select
                  className="select"
                  value={opNodeId}
                  onChange={(e) => setOpNodeId(e.target.value)}
                  disabled={busy || !selectedOpId || loadingNodes}
                >
                  <option value="">
                    {loadingNodes ? "Loading…" : selectedOpId ? "— select node —" : "— pick operation first —"}
                  </option>
                  {opNodes.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.label ? `${n.label} (${NODE_LABELS[n.type] ?? n.type})` : (NODE_LABELS[n.type] ?? n.type)}
                    </option>
                  ))}
                </select>
                {selectedOpId && !loadingNodes && opNodes.length === 0 && (
                  <div className="help">No schedulable nodes found in this operation.</div>
                )}
              </label>
            </>
          ) : type === "page" ? (
            <label className="field">
              <span className="label">Page (published)</span>
              <select
                className="select"
                value={selectedPageId}
                onChange={(e) => setSelectedPageId(e.target.value)}
                disabled={busy}
              >
                <option value="">Select a published page…</option>
                {publishedPages.map((p) => (
                  <option key={p.id} value={p.id}>{pageOptionLabel(p)}</option>
                ))}
              </select>
              {publishedPages.length === 0 && (
                <div className="help">No published pages yet.</div>
              )}
            </label>
          ) : type === "solve" ? (
            <label className="field">
              <span className="label">Optimization model</span>
              <select
                className="select"
                value={selectedModelId}
                onChange={(e) => setSelectedModelId(e.target.value)}
                disabled={busy}
              >
                <option value="">Select a model…</option>
                {optimizeModels.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
              <div className="help">
                When the schedule fires, this task runs a standing solve and succeeds only when a fresh
                plan lands — order it after the data pipelines it reads.
              </div>
              {optimizeModels.length === 0 && (
                <div className="help">No optimization models yet.</div>
              )}
            </label>
          ) : (
            <div className="help">
              Webhook and email task execution coming soon. The task will be created but will fail
              at runtime until implemented.
            </div>
          )}
        </div>
        <div className="actions">
          <button className="btn" type="button" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btnPrimary"
            type="button"
            onClick={() => void create()}
            disabled={busy || !valid}
          >
            Add task
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Run Now Modal
// =============================================================================

function RunNowModal(props: {
  workflowName: string;
  onConfirm: () => void;
  onClose: () => void;
  running: boolean;
}) {
  return (
    <div className="modalOverlay" role="presentation" onClick={props.onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="reportModalTitle">Run workflow now?</div>
        <p style={{ fontSize: 14, color: "var(--text-muted)", margin: "8px 0 0" }}>
          This will immediately start a run of <strong>{props.workflowName}</strong>. The run
          executes all tasks in order and can be monitored on the Activity page.
        </p>
        <div className="actions">
          <button className="btn" type="button" onClick={props.onClose} disabled={props.running}>
            Cancel
          </button>
          <button
            className="btn btnPrimary"
            type="button"
            onClick={props.onConfirm}
            disabled={props.running}
          >
            {props.running ? "Starting…" : "Run now"}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Main canvas component
// =============================================================================

export function ScheduleWorkflowCanvas() {
  const { id: workflowId = "" } = useParams<{ id: string }>();
  const nav = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workflow, setWorkflow] = useState<WorkflowDetail | null>(null);
  const [operations, setOperations] = useState<OpListItem[]>([]);
  const [publishedPages, setPublishedPages] = useState<PublishedPage[]>([]);
  const [optimizeModels, setOptimizeModels] = useState<OptimizeModelItem[]>([]);
  const [tenantTz, setTenantTz] = useState("UTC");
  // ADR-0068 caller context for <ScopeHeader>
  const [callerAccountId, setCallerAccountId] = useState<string | null>(null);
  const [callerRole, setCallerRole] = useState<"admin" | "builder" | "viewer">("viewer");
  const { groups: callerGroups } = useMyGroups();

  // ReactFlow state
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState<WfTaskNode>([]);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState<WfEdge>([]);

  // Selection
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);

  // Inspector state
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>("workflow");
  const [inspectorWidth, setInspectorWidth] = useState(300);
  const [inspectorOpen, setInspectorOpen] = useState(true);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const resizing = useRef(false);
  const resizeStartX = useRef(0);
  const resizeStartW = useRef(0);

  // UI state
  const [busy, setBusy] = useState(false);
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [runBusy, setRunBusy] = useState(false);

  // Held so Auto layout can fitView the result, as the pipeline editor does.
  const rfInstanceRef = useRef<ReactFlowInstance<WfTaskNode, WfEdge> | null>(null);

  // Keep canvasTasks as a parallel map for lookups
  const canvasTasksRef = useRef<Map<string, CanvasTask>>(new Map());
  const canvasEdgesRef = useRef<Map<string, CanvasEdgeData>>(new Map());

  // ---------------------------------------------------------------------------
  // Load
  // ---------------------------------------------------------------------------

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [wfData, opsData, settingsData, meData, pagesData, modelsData] = await Promise.all([
          apiFetch<{ workflow: WorkflowDetail }>(`/api/tenant/workflows/${encodeURIComponent(workflowId)}`),
          apiFetch<{ operations: OpListItem[] }>("/api/tenant/operations"),
          apiFetch<{ timezone: string }>("/api/tenant/settings"),
          apiFetch<{ user: { id: string; entitlement?: string } }>("/api/tenant/me").catch(() => null),
          // ADR-0104 — Report picker source (same endpoint subscriptions use).
          apiFetch<{ pages: PublishedPage[] }>("/api/tenant/pages/published").catch(() => ({ pages: [] })),
          // ADR-0185 — Solve picker source.
          apiFetch<{ models: OptimizeModelItem[] }>("/api/tenant/optimize/models").catch(() => ({ models: [] }))
        ]);

        if (meData) {
          setCallerAccountId(meData.user.id);
          const e = meData.user.entitlement;
          setCallerRole(e === "admin" ? "admin" : e === "builder" ? "builder" : "viewer");
        }

        const wf = wfData.workflow;
        setWorkflow(wf);
        setRfNodes(toRfNodes(wf.tasks));
        setRfEdges(toRfEdges(wf.edges));

        canvasTasksRef.current = new Map(wf.tasks.map((t) => [t.task_id, t]));
        canvasEdgesRef.current = new Map(wf.edges.map((e) => [e.id, e]));

        setOperations(opsData.operations);
        setPublishedPages(pagesData.pages ?? []);
        setOptimizeModels(modelsData.models ?? []);
        setTenantTz(settingsData.timezone ?? "UTC");
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, [workflowId]);

  // ---------------------------------------------------------------------------
  // Selection handlers
  // ---------------------------------------------------------------------------

  function handleNodeClick(_: React.MouseEvent, node: WfTaskNode) {
    setSelectedTaskId(node.id);
    setSelectedEdgeId(null);
    setInspectorTab("task");
  }

  function handleEdgeClick(_: React.MouseEvent, edge: WfEdge) {
    setSelectedEdgeId(edge.id);
    setSelectedTaskId(null);
    setInspectorTab("edge");
  }

  function handlePaneClick() {
    setSelectedTaskId(null);
    setSelectedEdgeId(null);
    setInspectorTab((prev) => (prev === "task" || prev === "edge" ? "workflow" : prev));
  }

  // ---------------------------------------------------------------------------
  // Node position save (on drag stop)
  // ---------------------------------------------------------------------------

  const handleNodeDragStop = useCallback(
    (_: React.MouseEvent, node: WfTaskNode) => {
      void (async () => {
        try {
          await apiFetch(
            `/api/tenant/workflows/${encodeURIComponent(workflowId)}/tasks/${encodeURIComponent(node.id)}`,
            { method: "PATCH", body: { position_x: Math.round(node.position.x), position_y: Math.round(node.position.y) } }
          );
          // Update canvasTasks ref
          const ct = canvasTasksRef.current.get(node.id);
          if (ct) {
            canvasTasksRef.current.set(node.id, { ...ct, position_x: node.position.x, position_y: node.position.y });
          }
        } catch {
          // position save failure is non-critical — don't toast
        }
      })();
    },
    [workflowId]
  );

  // ---------------------------------------------------------------------------
  // Edge connect
  // ---------------------------------------------------------------------------

  const handleConnect = useCallback(
    (connection: Connection) => {
      const { source, target } = connection;
      if (!source || !target) return;
      void (async () => {
        try {
          const data = await apiFetch<{ edge: CanvasEdgeData }>(
            `/api/tenant/workflows/${encodeURIComponent(workflowId)}/edges`,
            {
              method: "POST",
              body: { from_task_id: source, to_task_id: target, condition: "on_success" },
            }
          );
          const newEdge = data.edge;
          canvasEdgesRef.current.set(newEdge.id, newEdge);
          setRfEdges((prev) =>
            addEdge(
              {
                id: newEdge.id,
                source: newEdge.from_task_id,
                target: newEdge.to_task_id,
                label: conditionLabel(newEdge.condition),
                markerEnd: { type: MarkerType.ArrowClosed },
                data: { edgeId: newEdge.id, condition: newEdge.condition },
              },
              prev
            )
          );
          setWorkflow((wf) =>
            wf ? { ...wf, edges: [...wf.edges, newEdge] } : wf
          );
        } catch (e) {
          toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
        }
      })();
    },
    [workflowId, setRfEdges, toast]
  );

  // ---------------------------------------------------------------------------
  // Delete task
  // ---------------------------------------------------------------------------

  async function deleteTask() {
    if (!selectedTaskId || !workflow) return;
    const ct = canvasTasksRef.current.get(selectedTaskId);
    const taskName = ct?.task.name ?? "this task";
    const ok = await confirm.confirm({
      title: "Delete task?",
      detail: `This permanently deletes "${taskName}" and any edges connected to it.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiFetch(
        `/api/tenant/workflows/${encodeURIComponent(workflowId)}/tasks/${encodeURIComponent(selectedTaskId)}`,
        { method: "DELETE" }
      );
      setRfNodes((prev) => prev.filter((n) => n.id !== selectedTaskId));
      setRfEdges((prev) =>
        prev.filter((e) => e.source !== selectedTaskId && e.target !== selectedTaskId)
      );
      canvasTasksRef.current.delete(selectedTaskId);
      setWorkflow((wf) =>
        wf
          ? {
              ...wf,
              tasks: wf.tasks.filter((t) => t.task_id !== selectedTaskId),
              edges: wf.edges.filter(
                (e) => e.from_task_id !== selectedTaskId && e.to_task_id !== selectedTaskId
              ),
            }
          : wf
      );
      setSelectedTaskId(null);
      setInspectorTab("workflow");
      toast.push({ kind: "success", message: "Task deleted." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Delete edge
  // ---------------------------------------------------------------------------

  async function deleteEdge() {
    if (!selectedEdgeId) return;
    setBusy(true);
    try {
      await apiFetch(
        `/api/tenant/workflows/${encodeURIComponent(workflowId)}/edges/${encodeURIComponent(selectedEdgeId)}`,
        { method: "DELETE" }
      );
      setRfEdges((prev) => prev.filter((e) => e.id !== selectedEdgeId));
      canvasEdgesRef.current.delete(selectedEdgeId);
      setWorkflow((wf) =>
        wf ? { ...wf, edges: wf.edges.filter((e) => e.id !== selectedEdgeId) } : wf
      );
      setSelectedEdgeId(null);
      setInspectorTab("workflow");
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Task created callback
  // ---------------------------------------------------------------------------

  function handleTaskCreated(ct: CanvasTask) {
    canvasTasksRef.current.set(ct.task_id, ct);
    setRfNodes((prev) => [
      ...prev,
      {
        id: ct.task_id,
        type: "wfTask" as const,
        position: { x: ct.position_x, y: ct.position_y },
        data: { taskId: ct.task_id, name: ct.task.name, type: ct.task.type, operationName: ct.task.operation?.operation_name ?? null, opNodeLabel: ct.task.operation?.op_node_label ?? null, pageTitle: ct.task.page?.page_title ?? null, modelName: ct.task.solve?.model_name ?? null },
      },
    ]);
    setWorkflow((wf) => (wf ? { ...wf, tasks: [...wf.tasks, ct] } : wf));
    setSelectedTaskId(ct.task_id);
    setInspectorTab("task");
    toast.push({ kind: "success", message: "Task added." });
  }

  // ---------------------------------------------------------------------------
  // Auto-arrange
  // ---------------------------------------------------------------------------

  /**
   * Rank the board left to right along its edges — the same dagre layout the
   * pipeline editor runs, for the same reasons.
   *
   * This started as a grid and the grid was wrong twice over. It could not see
   * edges, so a schedule with dependencies came out scattered with connectors
   * cutting across rows; and it placed on a fixed pitch rather than measuring
   * anything, so nodes wider than the pitch overlapped and clipped each other.
   * dagre ranks along the edges and is given each node's REAL rendered size,
   * which is what stops both.
   */
  async function autoArrange() {
    const current = rfNodes;
    if (current.length === 0) return;

    const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
    g.setGraph({ rankdir: "LR", ranksep: 80, nodesep: 48 });
    for (const n of current) {
      g.setNode(n.id, { width: n.measured?.width ?? 240, height: n.measured?.height ?? 90 });
    }
    for (const e of rfEdges) {
      if (g.hasNode(e.source) && g.hasNode(e.target)) g.setEdge(e.source, e.target);
    }
    dagre.layout(g);

    const placed = new Map<string, { x: number; y: number }>();
    for (const n of current) {
      const p = g.node(n.id);
      if (!p) continue;
      // dagre reports centres; React Flow positions are top-left corners.
      const width = n.measured?.width ?? 240;
      const height = n.measured?.height ?? 90;
      placed.set(n.id, { x: Math.round(p.x - width / 2), y: Math.round(p.y - height / 2) });
    }
    if (placed.size === 0) return;

    // Show it, then persist — the pipeline editor's order. Positions are
    // cosmetic, so a failed save is worth a toast but not a snap-back.
    setRfNodes((prev) =>
      prev.map((n) => {
        const p = placed.get(n.id);
        return p ? { ...n, position: p } : n;
      })
    );
    window.requestAnimationFrame(() => {
      rfInstanceRef.current?.fitView({ padding: 0.2, duration: 300 });
    });

    setBusy(true);
    try {
      await Promise.all(
        [...placed].map(([taskId, pos]) =>
          apiFetch(
            `/api/tenant/workflows/${encodeURIComponent(workflowId)}/tasks/${encodeURIComponent(taskId)}`,
            { method: "PATCH", body: { position_x: pos.x, position_y: pos.y } }
          )
        )
      );
      for (const [taskId, pos] of placed) {
        const ct = canvasTasksRef.current.get(taskId);
        if (ct) canvasTasksRef.current.set(taskId, { ...ct, position_x: pos.x, position_y: pos.y });
      }
      setWorkflow((wf) =>
        wf
          ? {
              ...wf,
              tasks: wf.tasks.map((t) => {
                const p = placed.get(t.task_id);
                return p ? { ...t, position_x: p.x, position_y: p.y } : t;
              }),
            }
          : wf
      );
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Run now
  // ---------------------------------------------------------------------------

  async function runNow() {
    setRunBusy(true);
    try {
      await apiFetch(`/api/tenant/workflows/${encodeURIComponent(workflowId)}/run`, {
        method: "POST",
        body: {},
      });
      setRunModalOpen(false);
      toast.push({ kind: "success", message: "Workflow started. Check Activity for status." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setRunBusy(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Inspector resize
  // ---------------------------------------------------------------------------

  function onResizeMouseDown(e: React.MouseEvent) {
    e.preventDefault();
    resizing.current = true;
    resizeStartX.current = e.clientX;
    resizeStartW.current = inspectorWidth;

    function onMove(ev: MouseEvent) {
      if (!resizing.current) return;
      const delta = resizeStartX.current - ev.clientX;
      setInspectorWidth(Math.max(220, Math.min(500, resizeStartW.current + delta)));
    }
    function onUp() {
      resizing.current = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    }
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const selectedTask = selectedTaskId ? canvasTasksRef.current.get(selectedTaskId) ?? null : null;
  const selectedEdge = selectedEdgeId ? canvasEdgesRef.current.get(selectedEdgeId) ?? null : null;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) return <div className="card"><LoadingState /></div>;
  if (error || !workflow) return <div className="card"><ErrorState detail={error ?? "Not found"} /></div>;

  return (
    <div className="wfCanvasLayout">
      {/* Header */}
      <div className="wfCanvasHeader">
        <Link to="/app/schedule/workflows" className="muted" style={{ fontSize: 13, textDecoration: "none" }}>
          ← Schedules
        </Link>
        <span className="muted" style={{ fontSize: 13 }}>/</span>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{workflow.name}</span>
        <div style={{ flex: 1 }} />
        <button
          className="btn"
          type="button"
          onClick={() => setAddTaskOpen(true)}
        >
          + Add task
        </button>
        <button
          className="btn"
          type="button"
          style={{ padding: "4px 8px" }}
          title={inspectorOpen ? "Hide inspector" : "Show inspector"}
          onClick={() => setInspectorOpen((v) => !v)}
        >
          {inspectorOpen ? "▶" : "◀"}
        </button>
      </div>

      {/* ADR-0068 scope header. No Clone for workflows (schedule state baggage). */}
      {workflow.scope && (
        <div style={{ padding: "6px 10px" }}>
          <ScopeHeader
            entityKind="workflow"
            entityId={workflow.id}
            scope={workflow.scope}
            ownerGroupId={workflow.owner_group_id ?? null}
            ownerGroupName={workflow.owner_group_name ?? null}
            ownerAccountId={workflow.owner_account_id ?? null}
            callerAccountId={callerAccountId}
            callerRole={callerRole}
            callerGroupIds={callerGroups.map((g) => g.id)}
            onScopeChanged={async () => {
              // Re-fetch just the workflow's scope-related fields — task/edge
              // state is unchanged by a scope change, so no full-page reload.
              try {
                const r = await apiFetch<{ workflow: WorkflowDetail }>(
                  `/api/tenant/workflows/${encodeURIComponent(workflow.id)}`
                );
                setWorkflow((prev) => prev && ({
                  ...prev,
                  scope: r.workflow.scope,
                  owner_account_id: r.workflow.owner_account_id,
                  owner_group_id: r.workflow.owner_group_id,
                  owner_group_name: r.workflow.owner_group_name
                }));
              } catch {
                // Non-fatal — the scope chip may be stale until next page load.
              }
            }}
            disableClone
          />
        </div>
      )}

      {/* Canvas + inspector */}
      <div className="wfCanvasBody">
        {/* ReactFlow canvas */}
        <div className="wfCanvasArea">
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            nodeTypes={NODE_TYPES}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={handleConnect}
            onNodeClick={handleNodeClick}
            onEdgeClick={handleEdgeClick}
            onPaneClick={handlePaneClick}
            onNodeDragStop={handleNodeDragStop}
            onInit={(inst) => {
              rfInstanceRef.current = inst;
            }}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            deleteKeyCode={null}
          >
            <Background />
            <Controls />
            {rfNodes.length > 0 ? (
              <Panel position="top-left">
                <button
                  className="btn btnSm"
                  type="button"
                  onClick={() => void autoArrange()}
                  disabled={busy}
                  title="Arrange tasks left to right by dependency"
                >
                  Auto layout
                </button>
              </Panel>
            ) : null}
          </ReactFlow>
        </div>

        {/* Inspector */}
        {inspectorOpen ? (
          <>
            <div className="inspectorResizeHandle" onMouseDown={onResizeMouseDown} />
            <div
              className="pageBuilderInspector"
              style={{ width: inspectorWidth }}
            >
              {/* Inspector tabs */}
              <div className="inspectorHeader">
                {selectedTask ? (
                  <div className="inspectorTabs">
                    <button
                      className={`inspectorTab${inspectorTab === "task" ? " inspectorTabActive" : ""}`}
                      type="button"
                      onClick={() => setInspectorTab("task")}
                    >
                      Task
                    </button>
                  </div>
                ) : selectedEdge ? (
                  <div className="inspectorTabs">
                    <button
                      className={`inspectorTab${inspectorTab === "edge" ? " inspectorTabActive" : ""}`}
                      type="button"
                      onClick={() => setInspectorTab("edge")}
                    >
                      Edge
                    </button>
                  </div>
                ) : (
                  <div className="inspectorTabs">
                    <button
                      className={`inspectorTab${inspectorTab === "workflow" ? " inspectorTabActive" : ""}`}
                      type="button"
                      onClick={() => setInspectorTab("workflow")}
                    >
                      Workflow
                    </button>
                    <button
                      className={`inspectorTab${inspectorTab === "history" ? " inspectorTabActive" : ""}`}
                      type="button"
                      onClick={() => setInspectorTab("history")}
                    >
                      History
                    </button>
                  </div>
                )}
              </div>

              {/* Inspector content */}
              <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                {selectedTask && inspectorTab === "task" ? (
                  <TaskInspector
                    key={selectedTask.task_id}
                    task={selectedTask}
                    workflowId={workflowId}
                    operations={operations}
                    publishedPages={publishedPages}
                    optimizeModels={optimizeModels}
                    onSaved={(fields) => {
                      const ct = canvasTasksRef.current.get(selectedTask.task_id);
                      if (ct) {
                        const updated = { ...ct, task: { ...ct.task, ...fields } };
                        canvasTasksRef.current.set(selectedTask.task_id, updated);
                        if (fields.name !== undefined) {
                          setRfNodes((prev) =>
                            prev.map((n) =>
                              n.id === selectedTask.task_id
                                ? { ...n, data: { ...n.data, name: String(fields.name) } }
                                : n
                            )
                          );
                        }
                      }
                    }}
                    onDelete={() => void deleteTask()}
                    busy={busy}
                    setBusy={setBusy}
                    toast={toast}
                  />
                ) : selectedEdge && inspectorTab === "edge" ? (
                  <EdgeInspector
                    key={selectedEdge.id}
                    edge={selectedEdge}
                    workflowId={workflowId}
                    onSaved={(condition) => {
                      // Update edge label
                      canvasEdgesRef.current.set(selectedEdge.id, { ...selectedEdge, condition });
                      setRfEdges((prev) =>
                        prev.map((e) =>
                          e.id === selectedEdge.id
                            ? { ...e, label: conditionLabel(condition), data: { ...e.data!, condition } }
                            : e
                        )
                      );
                    }}
                    onDelete={() => void deleteEdge()}
                    busy={busy}
                    setBusy={setBusy}
                    toast={toast}
                  />
                ) : inspectorTab === "history" ? (
                  <WorkflowHistoryInspector
                    key={workflowId}
                    workflowId={workflowId}
                  />
                ) : (
                  <WorkflowInspector
                    key={workflowId}
                    workflow={workflow}
                    workflowId={workflowId}
                    timezone={tenantTz}
                    onSaved={(partial) => setWorkflow((wf) => (wf ? { ...wf, ...partial } : wf))}
                    onEditSchedule={() => setScheduleModalOpen(true)}
                    onRunNow={() => setRunModalOpen(true)}
                    busy={busy}
                    setBusy={setBusy}
                    toast={toast}
                  />
                )}
              </div>
            </div>
          </>
        ) : null}
      </div>

      {/* Add Task modal */}
      {addTaskOpen ? (
        <AddTaskModal
          workflowId={workflowId}
          operations={operations}
          publishedPages={publishedPages}
          optimizeModels={optimizeModels}
          existingTaskCount={workflow.tasks.length}
          onCreated={handleTaskCreated}
          onClose={() => setAddTaskOpen(false)}
        />
      ) : null}

      {/* Schedule modal */}
      {scheduleModalOpen && workflow ? (
        <ScheduleModal
          workflow={workflow}
          workflowId={workflowId}
          timezone={tenantTz}
          onSaved={(wf) => setWorkflow((prev) => (prev ? { ...prev, ...wf } : prev))}
          onClose={() => setScheduleModalOpen(false)}
        />
      ) : null}

      {/* Run Now modal */}
      {runModalOpen ? (
        <RunNowModal
          workflowName={workflow.name}
          onConfirm={() => void runNow()}
          onClose={() => setRunModalOpen(false)}
          running={runBusy}
        />
      ) : null}
    </div>
  );
}
